/**
 * «Conectar mi tienda» (2.1 §3): vendedores con claves de API o feeds, estado de su última
 * importación y bloqueo por vendedor (la API responde 403 y los feeds no se importan; no se borra
 * nada). El interruptor general es `seller_api` en Sistema.
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@nuvvu/shared/api';
import type { SellerApiAdminSeller } from '@nuvvu/shared';
import { call, errorText } from '../api.js';
import { btn, card, muted, Notice, Table, td, when } from '../lib/ui.js';

const RUN: Record<string, string> = {
  done: 'Completada',
  partial: 'Parcial',
  failed: 'Fallida',
  running: 'En curso',
  queued: 'En cola',
};

function lastRunText(s: SellerApiAdminSeller): string {
  const runs = s.feeds.map((f) => f.last_run).filter((r) => r !== null);
  if (runs.length === 0) return s.feeds.length ? 'Sin importar aún' : '—';
  const r = runs.sort((a, b) => (b.finished_at_ms ?? 0) - (a.finished_at_ms ?? 0))[0];
  if (!r) return '—';
  const c = r.counts;
  return `${RUN[r.status] ?? r.status} · ${when(r.finished_at_ms ?? 0)} · ${String(c.created)} nuevos, ${String(c.updated)} act., ${String(c.failed)} errores${r.error ? ` · ${r.error}` : ''}`;
}

export function SellerApiPage(): ReactElement {
  const [rows, setRows] = useState<SellerApiAdminSeller[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const load = useCallback(() => {
    setError(null);
    call(api.sellerApi.adminOverview, {})
      .then((o) => {
        setRows(o.sellers);
      })
      .catch((e: unknown) => {
        setError(errorText(e));
        setRows([]);
      });
  }, []);
  useEffect(load, [load]);

  const toggle = (s: SellerApiAdminSeller): void => {
    const blocked = !s.blocked;
    const reason = blocked
      ? (window.prompt('Motivo del bloqueo (lo verá el equipo):') ?? null)
      : '';
    if (reason === null) return;
    setBusy(s.uid);
    call(api.sellerApi.adminSetAccess, { uid: s.uid, blocked, reason })
      .then(load)
      .catch((e: unknown) => {
        setError(errorText(e));
      })
      .finally(() => {
        setBusy(null);
      });
  };

  return (
    <>
      <h1 style={{ margin: 0 }}>Conectar mi tienda</h1>
      <p style={muted}>
        Vendedores con claves de API o feeds CSV/XML. Bloquear corta la API (403) y las
        importaciones de ese vendedor; no borra claves, feeds ni anuncios. Requisito: PRO +
        profesional verificado. El interruptor general es «Conectar mi tienda» en Sistema.
      </p>
      <Notice text={error} error />
      {!rows && <p style={muted}>Cargando…</p>}
      {rows && (
        <div style={card}>
          <Table
            head={[
              'Vendedor',
              'Requisitos',
              'Claves',
              'Último uso API',
              'Feeds',
              'Última importación',
              'Anuncios',
              'Acceso',
            ]}
          >
            {rows.map((s) => (
              <tr key={s.uid}>
                <td style={td}>
                  <Link to={`/usuarios/${s.uid}`}>{s.username ? `@${s.username}` : s.uid}</Link>
                </td>
                <td style={td}>
                  {s.is_pro ? 'PRO' : 'sin PRO'} · {s.is_trader ? 'profesional' : 'no verificado'}
                </td>
                <td style={td}>{s.active_keys}</td>
                <td style={td}>{s.last_used_at_ms ? when(s.last_used_at_ms) : '—'}</td>
                <td style={td}>
                  {s.feeds.length === 0
                    ? '—'
                    : s.feeds.map((f) => (
                        <div key={f.feed_id} style={{ wordBreak: 'break-all' }}>
                          {f.enabled ? '🟢' : '⚪'} {f.url}
                        </div>
                      ))}
                </td>
                <td style={td}>{lastRunText(s)}</td>
                <td style={td}>{s.items_count}</td>
                <td style={td}>
                  {s.blocked && (
                    <div style={{ ...muted, color: '#991B1B' }}>
                      Bloqueado{s.blocked_reason ? `: ${s.blocked_reason}` : ''}
                    </div>
                  )}
                  <button
                    type="button"
                    style={btn(false, !s.blocked)}
                    disabled={busy === s.uid}
                    onClick={() => {
                      toggle(s);
                    }}
                  >
                    {s.blocked ? 'Desbloquear' : 'Bloquear'}
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td style={td} colSpan={8}>
                  Nadie ha conectado su tienda todavía.
                </td>
              </tr>
            )}
          </Table>
        </div>
      )}
    </>
  );
}
